/**
 * Quantity Break Block — PDP Theme Block
 *
 * Manages the quantity-break tier selection UI, cart form state, price display,
 * variant synchronisation, and Shopify theme-editor integration.
 *
 * Data contract (set by the inline Liquid bridge in pdp-quantity-break-block.liquid):
 *   window.__qbBlockData[blockId] = { moneyFormat, allVariants, defaultVariant,
 *                                     hasVariants, optionNames }
 */
(function () {
  "use strict";

  /* ─────────────────────────────────────────────────────────────────────────
   * 1. INITIALIZATION & CONFIG
   * ───────────────────────────────────────────────────────────────────────── */

  function initBlock(rootEl) {
    if (!rootEl) return;

    const blockId = rootEl.dataset.blockId;
    if (!blockId) return;

    const data = (window.__qbBlockData && window.__qbBlockData[blockId]) || {};
    const moneyFormat = data.moneyFormat || "";
    const allVariants = data.allVariants || [];
    const defaultVariant = data.defaultVariant || allVariants[0] || null;
    const hasVariants = !!data.hasVariants;
    const optionNames = data.optionNames || [];

    const enableVS = rootEl.dataset.enableVs === "true";
    const syncGallery = rootEl.dataset.syncGallery === "true";
    const preselected = rootEl.dataset.preselected || "first_visible";
    const saveBadgeText = rootEl.dataset.saveBadgeText || "SAVE {savings_amount}";
    const savingsFormat = rootEl.dataset.savingsFormat || "You save {savings}";

    /* ─────────────────────────────────────────────────────────────────────
     * 2. VARIANT SELECTION ANIMATION
     *
     * Open/close is pure CSS: the .quantity-break-variant-container animates
     * grid-template-rows 0fr → 1fr (see block CSS), driven entirely by the
     * .selected class on the option. No JS height measuring is needed — the
     * grid auto-sizes to content, so variant/qty changes just work.
     *
     * The only JS responsibility is gating the transition on first paint:
     * applyPreselection() adds the .qb-anim-ready class on the next frame so the
     * preselected tier is open with no animation flash on load.
     * ───────────────────────────────────────────────────────────────────── */

    /* ─────────────────────────────────────────────────────────────────────
     * 3. IMAGE DROPDOWN (variant picker)
     *
     * Each option position renders a hidden native <select> (the single source
     * of truth read by the variant resolver) plus a custom button + listbox.
     * Selecting an option writes the value back to the select and dispatches a
     * native `change` so the existing cart/price pipeline runs unchanged.
     * ───────────────────────────────────────────────────────────────────── */

    function closeAllDropdowns(except) {
      rootEl.querySelectorAll(".qb-imgdd").forEach(function (dd) {
        if (dd === except) return;
        const b = dd.querySelector(".qb-imgdd-btn");
        const l = dd.querySelector(".qb-imgdd-list");
        if (b) b.setAttribute("aria-expanded", "false");
        if (l) l.hidden = true;
      });
    }

    /* ─── Built-in swatch color resolver ──────────────────────────────────
     * Maps common color name fragments to CSS color strings.
     * Used when layout=swatches and an option has no variant images.
     * No merchant mapping required — purely CSS-name + extended palette.
     * ─────────────────────────────────────────────────────────────────── */
    var SWATCH_COLORS = {
      black: "#111111", white: "#f5f5f5", ivory: "#fffff0", cream: "#fffdd0",
      red: "#e53935", crimson: "#c62828", scarlet: "#ff2400", burgundy: "#800020",
      pink: "#e91e63", hotpink: "#ff69b4", rose: "#f43f5e", blush: "#ffb7c5",
      orange: "#fb8c00", coral: "#ff7043", peach: "#ffcba4",
      amber: "#ffb300", yellow: "#fdd835", gold: "#ffc107", mustard: "#e3a857",
      green: "#43a047", lime: "#c6ef3a", mint: "#98d8c8", sage: "#9caf88",
      olive: "#808000", forest: "#228b22", emerald: "#50c878",
      teal: "#00897b", cyan: "#00acc1", aqua: "#00bcd4", turquoise: "#40e0d0",
      blue: "#1e88e5", navy: "#1a237e", cobalt: "#005cba", denim: "#1560bd",
      sky: "#87ceeb", steel: "#4682b4", indigo: "#3949ab", royal: "#4169e1",
      purple: "#8e24aa", violet: "#7e57c2", lavender: "#e6e0f5", lilac: "#c9a0dc",
      plum: "#8e4585", mauve: "#e0b0b7",
      cherry: "#b3123a", berry: "#8e2453", raspberry: "#c21e56", ruby: "#9b111e",
      wine: "#722f37", fuchsia: "#d61a84", magenta: "#c2185b", nude: "#e3bc9a",
      brown: "#6d4c41", tan: "#d2b48c", beige: "#f5f0e8", camel: "#c19a6b",
      sand: "#e8dcc8", khaki: "#c3b091", mocha: "#967259", chocolate: "#7b3f00",
      grey: "#9e9e9e", gray: "#9e9e9e", silver: "#bdbdbd", charcoal: "#4a4a4a",
      slate: "#607d8b", stone: "#928e85",
      multicolor: "linear-gradient(135deg,#e53935,#fb8c00,#43a047,#1e88e5)",
      "multi-color": "linear-gradient(135deg,#e53935,#fb8c00,#43a047,#1e88e5)",
      multi: "linear-gradient(135deg,#e53935,#fb8c00,#43a047,#1e88e5)",
      rainbow: "linear-gradient(135deg,#e53935,#fb8c00,#fdd835,#43a047,#1e88e5,#8e24aa)",
    };

    function resolveSwatchColor(val) {
      var key = (val || "").toLowerCase().replace(/[-_\s]+/g, " ").trim();
      if (SWATCH_COLORS[key]) return SWATCH_COLORS[key];
      // Fragment match: "dark blue" → find "blue" key
      var keys = Object.keys(SWATCH_COLORS);
      for (var i = 0; i < keys.length; i++) {
        if (key.indexOf(keys[i]) !== -1) return SWATCH_COLORS[keys[i]];
      }
      // Last resort: the raw value, if the browser knows it as a CSS color name
      var named = key.replace(/\s+/g, "");
      if (window.CSS && CSS.supports && CSS.supports("color", named)) return named;
      return null;
    }

    /* ─── initLayout ───────────────────────────────────────────────────────
     * Reads data-layout + data-visual-opt from the root element and applies
     * qb-opt-visual / qb-opt-text classes to each option wrapper so CSS can
     * show/hide thumbnails and swatch chips without extra data attributes.
     * Also applies built-in color dots for color-named options in swatch mode.
     * ─────────────────────────────────────────────────────────────────── */
    function initLayout() {
      var layout = rootEl.dataset.layout || "dropdown";
      var visualOpt = rootEl.dataset.visualOpt || "auto";

      rootEl.querySelectorAll(".quantity-break-variant-option").forEach(function (wrapper) {
        var imgDd = wrapper.querySelector(".qb-imgdd");
        var optPos = imgDd ? imgDd.dataset.optionPosition : null;
        var imgBearing = wrapper.dataset.imageBearing === "true";

        var isVisual = false;
        if (layout !== "text" && visualOpt !== "none") {
          isVisual = visualOpt === "auto" ? imgBearing : optPos === visualOpt;
        }

        wrapper.classList.toggle("qb-opt-visual", isVisual);
        wrapper.classList.toggle("qb-opt-text", !isVisual);

        // In swatch mode, apply CSS color dots for color-named options without images
        if (layout === "swatches" && isVisual && !imgBearing) {
          var selectEl = wrapper.querySelector(".qb-imgdd-select");
          var optName = selectEl ? selectEl.dataset.optionName || "" : "";
          if (/colou?r|shade/i.test(optName)) {
            wrapper.querySelectorAll(".qb-swatch").forEach(function (chip) {
              // Already coloured by a variant image, a native swatch or the theme's colour list
              if (chip.style.backgroundImage || chip.dataset.swatchResolved === "true") return;
              var color = resolveSwatchColor(chip.dataset.value || "");
              if (!color) return;
              if (color.indexOf("gradient") !== -1) {
                chip.style.background = color;
              } else {
                chip.style.backgroundColor = color;
              }
              var init = chip.querySelector(".qb-swatch-init");
              if (init) init.textContent = "";
            });
          }
        }
      });
    }

    /* ─── syncSwatches ─────────────────────────────────────────────────────
     * Mirrors a select's current value onto its swatch chips' aria-selected.
     * ─────────────────────────────────────────────────────────────────── */
    function syncSwatches(selectEl) {
      var dd = selectEl.closest(".qb-imgdd");
      if (!dd) return;
      var val = selectEl.value;
      dd.querySelectorAll(".qb-swatch").forEach(function (chip) {
        chip.setAttribute("aria-selected", chip.dataset.value === val ? "true" : "false");
      });
    }

    /* ─── initSwatches ─────────────────────────────────────────────────────
     * Binds click handlers on swatch chip groups. Clicking a chip updates the
     * hidden select (source of truth) and dispatches a native change event so
     * the existing cart/price pipeline runs unchanged.
     * ─────────────────────────────────────────────────────────────────── */
    function initSwatches() {
      rootEl.querySelectorAll(".qb-swatches").forEach(function (swatchGroup) {
        if (swatchGroup.__qbSwInit) return;
        swatchGroup.__qbSwInit = true;

        swatchGroup.addEventListener("click", function (e) {
          var chip = e.target.closest(".qb-swatch");
          if (!chip) return;
          e.preventDefault();
          e.stopPropagation();
          if (chip.getAttribute("aria-disabled") === "true") return;

          var val = chip.dataset.value;
          var dd = swatchGroup.closest(".qb-imgdd");
          if (!dd) return;
          var select = dd.querySelector(".qb-imgdd-select");
          if (!select) return;

          if (select.value !== val) {
            select.value = val;
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
          syncSwatches(select);
          syncImageDropdownButton(select);
        });

        // Initial sync with the select's current value
        var dd = swatchGroup.closest(".qb-imgdd");
        if (dd) {
          var select = dd.querySelector(".qb-imgdd-select");
          if (select) syncSwatches(select);
        }
      });
    }

    // Refresh a dropdown button (thumbnail + label + aria-selected) from its
    // select's current value. Pure DOM — safe to call after programmatic changes.
    function syncImageDropdownButton(selectEl) {
      const dd = selectEl.closest(".qb-imgdd");
      if (!dd) return;
      const val = selectEl.value;
      const btn = dd.querySelector(".qb-imgdd-btn");
      const list = dd.querySelector(".qb-imgdd-list");
      if (!btn || !list) return;
      let selLi = null;
      list.querySelectorAll(".qb-imgdd-opt").forEach(function (li) {
        const isSel = li.dataset.value === val;
        li.setAttribute("aria-selected", isSel ? "true" : "false");
        if (isSel) selLi = li;
      });
      if (!selLi) return;
      const btnThumb = btn.querySelector(".qb-imgdd-thumb");
      const srcThumb = selLi.querySelector(".qb-imgdd-thumb");
      if (btnThumb && srcThumb) btnThumb.innerHTML = srcThumb.innerHTML;
      const cur = btn.querySelector(".qb-imgdd-current");
      if (cur) cur.textContent = val;
    }

    function initImageDropdowns() {
      rootEl.querySelectorAll(".qb-imgdd").forEach(function (dd) {
        if (dd.__qbDdInit) return;
        dd.__qbDdInit = true;
        const select = dd.querySelector(".qb-imgdd-select");
        const btn = dd.querySelector(".qb-imgdd-btn");
        const list = dd.querySelector(".qb-imgdd-list");
        if (!select || !btn || !list) return;

        btn.addEventListener("click", function (e) {
          e.preventDefault();
          e.stopPropagation();
          const willOpen = list.hidden;
          closeAllDropdowns(dd);
          list.hidden = !willOpen;
          btn.setAttribute("aria-expanded", willOpen ? "true" : "false");
        });

        list.addEventListener("click", function (e) {
          const li = e.target.closest(".qb-imgdd-opt");
          if (!li) return;
          e.preventDefault();
          e.stopPropagation();
          if (li.getAttribute("aria-disabled") === "true") return;
          const val = li.dataset.value;
          if (select.value !== val) {
            select.value = val;
            select.dispatchEvent(new Event("change", { bubbles: true }));
          }
          syncImageDropdownButton(select);
          list.hidden = true;
          btn.setAttribute("aria-expanded", "false");
        });

        // Keep button + swatches in sync whenever the select changes (user or programmatic)
        select.addEventListener("change", function () {
          syncImageDropdownButton(select);
          syncSwatches(select);
        });

        syncImageDropdownButton(select);
      });

      if (!rootEl.__qbDdDocBound) {
        rootEl.__qbDdDocBound = true;
        addDocListener("click", function () {
          closeAllDropdowns(null);
        });
        addDocListener("keydown", function (e) {
          if (e.key === "Escape") closeAllDropdowns(null);
        });
      }
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 3. MONEY FORMATTING
     * ───────────────────────────────────────────────────────────────────── */

    function formatMoney(cents) {
      if (typeof Elixir_FormatMoney === "function") return Elixir_FormatMoney(cents, moneyFormat);
      // Fallback: use shop currency from Shopify global if available
      var currency = (typeof Shopify !== "undefined" && Shopify.currency && Shopify.currency.active) || "USD";
      return new Intl.NumberFormat("en-US", { style: "currency", currency: currency }).format(cents / 100);
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 4. VARIANT RESOLUTION
     * ───────────────────────────────────────────────────────────────────── */

    function getOptionVal(qtyOpt, itemIdx, pos) {
      // QB-specific radio
      const r = rootEl.querySelector(
        'input[data-quantity-option="' +
          qtyOpt +
          '"][data-item-index="' +
          itemIdx +
          '"][data-option-selector="' +
          pos +
          '"]:checked',
      );
      if (r) return (r.dataset.optionValue || r.value || "").trim();

      // QB-specific select
      const s = rootEl.querySelector(
        '.quantity-break-variant-select[data-quantity-option="' +
          qtyOpt +
          '"][data-item-index="' +
          itemIdx +
          '"][data-option-selector="' +
          pos +
          '"]',
      );
      if (s && s.value) return s.value.trim();

      // Fall back to simple-variant-picker
      const svpR = document.querySelector('.simple-variant-picker__radio[data-option-position="' + pos + '"]:checked');
      if (svpR) return svpR.value.trim();
      const svpS = document.querySelector('.simple-variant-picker__select[data-option-position="' + pos + '"]');
      if (svpS && svpS.value) return svpS.value.trim();

      return "";
    }

    function pickVariant(qtyOpt, itemIdx) {
      if (!hasVariants) return defaultVariant;
      const sel = {};
      let any = false;
      for (let p = 1; p <= optionNames.length; p++) {
        const v = getOptionVal(qtyOpt, itemIdx, p);
        if (v) {
          sel["option" + p] = v;
          any = true;
        }
      }
      if (!any) return defaultVariant;
      return (
        allVariants.find(function (v) {
          return Object.keys(sel).every(function (k) {
            return v[k] === sel[k];
          });
        }) || defaultVariant
      );
    }

    function getCurrentVariant() {
      // Prefer the form's current variant id
      if (typeof Elixir_GetProductFormVariantId === "function") {
        const fid = Elixir_GetProductFormVariantId();
        if (fid != null && fid !== "") {
          const fv = allVariants.find(function (v) {
            return String(v.id) === String(fid);
          });
          if (fv) return fv;
        }
      }
      // Fall back to SVP selection
      if (hasVariants) {
        const sel = {};
        for (let p = 1; p <= optionNames.length; p++) {
          const r = document.querySelector('.simple-variant-picker__radio[data-option-position="' + p + '"]:checked');
          const s = document.querySelector('.simple-variant-picker__select[data-option-position="' + p + '"]');
          const v = r ? r.value : s ? s.value : null;
          if (v) sel["option" + p] = v;
        }
        if (Object.keys(sel).length) {
          const found = allVariants.find(function (v) {
            return Object.keys(sel).every(function (k) {
              return v[k] === sel[k];
            });
          });
          if (found) return found;
        }
      }
      return defaultVariant;
    }

    function getSelectedVariants() {
      const inp = rootEl.querySelector(".quantity-break-option.selected input.quantity-break-input");
      if (!inp) return [[], 0, 0];
      const qty = parseInt(inp.value, 10) || 1;
      if (qty <= 1) {
        const v = pickVariant(qty, 1);
        return [[v.id], v.price, v.compare_at_price || 0];
      }
      const ids = [];
      let total = 0,
        cmpTotal = 0;
      for (let i = 1; i <= qty; i++) {
        const v = pickVariant(qty, i);
        ids.push(v.id);
        total += v.price;
        cmpTotal += v.compare_at_price || 0;
      }
      return [ids, total, cmpTotal];
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 5. PRICE ENGINE
     * ───────────────────────────────────────────────────────────────────── */

    function calcCustomPrice(vPrice, qty, fmt, pct, amt, bogo) {
      switch (fmt) {
        case "percentage":
          if (pct > 0 && pct <= 100) return Math.round(vPrice * qty * (1 - pct / 100));
          return vPrice * qty;
        case "amount":
          if (amt !== "") {
            const a = parseFloat(amt);
            if (!isNaN(a)) return Math.round(a * 100);
          }
          return vPrice * qty;
        case "bogo":
          if (bogo > 0) return Math.round(vPrice * bogo);
          return vPrice * qty;
        default:
          return vPrice * qty;
      }
    }

    // rAF-batched price refresh — prevents redundant repaints when multiple
    // events fire in the same tick (e.g. SVP change + quantity:changed).
    let rafPending = false;
    function scheduleRefresh() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        refreshPrices();
      });
    }

    function refreshPrices() {
      if (typeof Elixir_GetDisplayPrice !== "function") return;
      const variant = getCurrentVariant();
      if (!variant) return;

      const vPrice = variant.price;
      const vCmp = variant.compare_at_price || 0;

      rootEl.querySelectorAll(".quantity-break-option-container").forEach(function (container) {
        const inp = container.querySelector("input.quantity-break-input");
        if (!inp) return;

        const qty = Math.max(1, parseInt(inp.dataset.optionQuantity || inp.value, 10) || 1);
        const hasCustom = inp.dataset.enableCustomPricing === "true";
        let priceCents, cmpCents;

        const cmpOvr = inp.dataset.customCompareAtPrice || "";

        if (hasCustom) {
          const fmt = inp.dataset.customPriceFormat || "amount";
          const pct = parseFloat(inp.dataset.customPricePercentage) || 0;
          const amt = inp.dataset.customPriceAmount || "";
          const bogo = parseFloat(inp.dataset.bogoMultiplier) || 0;
          priceCents = calcCustomPrice(vPrice, qty, fmt, pct, amt, bogo);
          // When no explicit compare-at is configured, fall back to the undiscounted
          // base price so the savings badge reflects the discount correctly.
          cmpCents = cmpOvr !== "" ? Math.round(parseFloat(cmpOvr) * 100) : vCmp * qty || vPrice * qty;
        } else {
          priceCents = vPrice * qty;
          cmpCents = cmpOvr !== "" ? Math.round(parseFloat(cmpOvr) * 100) : vCmp * qty;
        }

        const result = Elixir_GetDisplayPrice({
          variantId: variant.id,
          price: priceCents,
          compareAtPrice: cmpCents,
          quantity: qty,
          multiplyByQuantity: false,
          applySubscriptionDiscount: true,
        });

        const priceEl = container.querySelector(".quantity-break-price");
        const cmpEl = container.querySelector(".quantity-break-original-price");

        if (priceEl) priceEl.textContent = formatMoney(result.price);

        if (cmpEl) {
          if (result.compareAtPrice > result.price) {
            cmpEl.textContent = formatMoney(result.compareAtPrice);
            cmpEl.style.visibility = "";
          } else {
            cmpEl.style.visibility = "hidden";
          }
        }

        const savingsCents = Math.max(0, result.compareAtPrice - result.price);

        const savingsEl = container.querySelector(".quantity-break-savings[data-savings-qty]");
        if (savingsEl) {
          const customTpl = savingsEl.dataset.savingsCustom || "";
          const tpl = customTpl || savingsFormat;
          // Per-tier pricing tokens — all reflect the resolved custom/subscription
          // price. Replace longest/specific tokens first so shared prefixes
          // (e.g. {savings} vs {savings_amount}) don't clobber each other.
          const perItemCents = qty > 0 ? Math.round(result.price / qty) : result.price;
          const perItemCompareCents = qty > 0 ? Math.round(result.compareAtPrice / qty) : result.compareAtPrice;
          const savingsPercent = result.compareAtPrice > 0 ? Math.round((savingsCents * 100) / result.compareAtPrice) : 0;
          const subVars = {
            "{savings_amount}": formatMoney(savingsCents),
            "{savings_percent}": savingsPercent + "%",
            "{savings}": formatMoney(savingsCents),
            "{price_each}": formatMoney(perItemCents),
            "{price}": formatMoney(result.price),
            "{compare_each}": formatMoney(perItemCompareCents),
            "{compare}": formatMoney(result.compareAtPrice),
            "{qty}": String(qty),
          };
          let subHtml = tpl;
          Object.keys(subVars).forEach(function (token) {
            subHtml = subHtml.split(token).join(subVars[token]);
          });
          savingsEl.innerHTML = subHtml;
        }

        const badge = container.querySelector(".quantity-break-save-badge");
        if (badge) {
          if (savingsCents <= 0) {
            badge.style.display = "none";
          } else {
            badge.style.display = "";
            var pct = (result.compareAtPrice > 0 ? Math.round((savingsCents * 100) / result.compareAtPrice) : 0) + "%";
            var amt = formatMoney(savingsCents);
            // Per-option override (set on the option container) wins over the parent block's format.
            var badgeFmt = container.dataset.saveBadgeText || saveBadgeText;
            badge.textContent = badgeFmt.replace("{savings_percent}", pct).replace("{savings_amount}", amt);
          }
        }

        // Offer Bar text variables — substitute per-option pricing into any text bars
        const barTexts = container.querySelectorAll(".qb-bar-text[data-qb-bar-tpl]");
        if (barTexts.length) {
          const barVars = {
            "{savings}": formatMoney(savingsCents),
            "{savings_amount}": formatMoney(savingsCents),
            "{savings_percent}": (result.compareAtPrice > 0 ? Math.round((savingsCents * 100) / result.compareAtPrice) : 0) + "%",
            "{qty}": String(qty),
          };
          barTexts.forEach(function (el) {
            let html = el.dataset.qbBarTpl || "";
            Object.keys(barVars).forEach(function (token) {
              html = html.split(token).join(barVars[token]);
            });
            el.innerHTML = html;
          });
        }

        // Publish the selected tier's final totals to the shared price-override
        // channel so the price block and ATC button reflect the QB custom price.
        const isSelected = container.querySelector(".quantity-break-option.selected") !== null;
        if (isSelected && window.Elixir_PriceOverride) {
          if (getLiveFormQuantity(qty) === qty) {
            window.Elixir_PriceOverride.set({
              price: result.price,
              compareAtPrice: result.compareAtPrice,
              source: blockId,
            });
          } else {
            window.Elixir_PriceOverride.clear(blockId);
          }
        }
      });
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 7. CART FORM MANAGEMENT
     * ───────────────────────────────────────────────────────────────────── */

    function getProductForm() {
      if (typeof Elixir_GetProductForm === "function") return Elixir_GetProductForm();
      return null;
    }

    function getLiveFormQuantity(fallback) {
      const form = getProductForm();
      if (!form) return fallback;
      let input = form.querySelector('input[name="quantity"]');
      if (!input && form.getAttribute('id')) {
        input = document.querySelector('input[name="quantity"][form="' + form.getAttribute('id') + '"]');
      }
      const n = input ? parseInt(input.value, 10) : NaN;
      return isNaN(n) || n < 1 ? fallback : n;
    }

    function updateVariantSelections() {
      const [ids, ,] = getSelectedVariants();
      if (!ids.length) return;

      // Append gift variant IDs from any free_gift bars inside the selected option
      const selOpt = rootEl.querySelector(".quantity-break-option.selected");
      if (selOpt) {
        selOpt.querySelectorAll(".qb-bar[data-bar-type='free_gift']").forEach(function (bar) {
          // Respect the per-bar "Auto-add gift to cart" toggle — when off the
          // bar is display-only (gift added manually, via an app, or in cart).
          if (bar.dataset.giftAdd === "false") return;
          const vid = bar.dataset.giftVariantId;
          const gQty = parseInt(bar.dataset.giftQty || "1", 10);
          if (vid) {
            for (let g = 0; g < gQty; g++) ids.push(vid);
          }
        });
      }

      const inp = rootEl.querySelector(".quantity-break-option.selected input.quantity-break-input");
      const hasCustom = inp && inp.dataset.enableCustomPricing === "true";
      const customPrice = inp ? parseInt(inp.dataset.price || "0", 10) : 0;
      const qtyOpt = inp ? parseInt(inp.value, 10) : 1;

      // Offset index past any premium kit inputs already in the form
      const premiumLen = document.querySelectorAll(".product-variant-premium-kit").length;
      const startIdx = premiumLen + 1;

      // Current selling plan from subscription context
      const subCtx = window.subscriptionContext;
      const tierPlan = inp && inp.dataset.sellingPlanId ? String(inp.dataset.sellingPlanId).trim() : "";
      const sellingPlan =
        subCtx && subCtx.sellingPlanId ? tierPlan || String(subCtx.sellingPlanId) : null;

      // Deduplicate variant IDs → items list
      const quantityList = Object.values(
        ids.reduce(function (acc, id) {
          if (!acc[id]) acc[id] = { id: id, quantity: 1 };
          else acc[id].quantity++;
          return acc;
        }, {}),
      );

      const needsItems = quantityList.length > 1 || hasCustom;

      if (!needsItems) {
        // Single-variant mode: restore input[name="id"]
        if (typeof Elixir_ProductFormSwitchToSingleIdMode === "function") {
          Elixir_ProductFormSwitchToSingleIdMode(undefined, ".quantity-break-variant-form");
        }
        // Ensure the variant ID is current
        if (quantityList[0] && typeof Elixir_SetProductFormVariantId === "function") {
          Elixir_SetProductFormVariantId(quantityList[0].id);
        }
        if (typeof Elixir_SetProductFormSellingPlan === "function") {
          Elixir_SetProductFormSellingPlan(sellingPlan || "");
        }
      } else {
        // Multi-line items[] mode
        const items = quantityList.map(function (_item, i) {
          const props = {};
          if (hasCustom && customPrice > 0 && i === 0) {
            props._custom_total_price = (customPrice / 100).toFixed(2);
            props._quantity_option = String(qtyOpt);
          }
          const item = { id: _item.id, quantity: _item.quantity };
          if (Object.keys(props).length) item.properties = props;
          if (sellingPlan) item.selling_plan = sellingPlan;
          return item;
        });

        if (typeof Elixir_ProductFormSwitchToItemsMode === "function") {
          Elixir_ProductFormSwitchToItemsMode(items, {
            containerClass: "quantity-break-variant-form",
            startIndex: startIdx,
          });
        }
      }

      scheduleRefresh();
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 8. SELECTION & PRESELECTION
     * ───────────────────────────────────────────────────────────────────── */

    // Form-dependent tail of a selection: sync the radio, push quantity into the
    // product form, rebuild the items[] payload, and broadcast the change. Shared
    // by user clicks (selectOption) and the initial commit (commitToForm) so
    // neither path re-runs the visual open animation.
    function commitSelection(optEl) {
      const inp = optEl.querySelector("input.quantity-break-input");
      if (inp) inp.checked = true;

      const qty = inp ? parseInt(inp.value, 10) : 1;

      // Use the shared helper so quantity:changed fires correctly
      if (typeof Elixir_SetProductFormQuantity === "function") {
        Elixir_SetProductFormQuantity(qty, { source: blockId });
      } else {
        // Minimal fallback: set form quantity directly
        const form = getProductForm();
        if (form) {
          let qInp = form.querySelector('input[name="quantity"]');
          if (!qInp) {
            qInp = document.createElement("input");
            qInp.type = "hidden";
            qInp.name = "quantity";
            form.appendChild(qInp);
          }
          qInp.value = qty;
          qInp.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      updateVariantSelections();

      document.dispatchEvent(
        new CustomEvent("quantity_break:changed", {
          detail: {
            quantity: qty,
            price: inp ? parseInt(inp.dataset.price || "0", 10) : 0,
            blockId: blockId,
          },
        }),
      );
    }

    function selectOption(optEl) {
      // Open/close is fully CSS-driven off the .selected class (grid-template-rows).
      rootEl.querySelectorAll(".quantity-break-option").forEach(function (o) {
        if (o === optEl) return;
        o.classList.remove("selected");
      });
      optEl.classList.add("selected");

      // Defer the heavy commit (cart-form rebuild + reprice) until after the
      // browser has painted the first animation frame(s). Running it inline on the
      // click frame forces a synchronous reflow right as the open/close transition
      // starts, which drops frames and makes the animation feel laggy. Two rAFs
      // clear the first paint; the stale-selection guard skips commits that a
      // faster subsequent click has already superseded.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (!optEl.classList.contains("selected")) return;
          commitSelection(optEl);
        });
      });
    }

    function applyPreselection() {
      const opts = Array.from(rootEl.querySelectorAll(".quantity-break-option"));
      if (!opts.length) return;
      let target;
      if (preselected === "first_visible") {
        target = opts[0];
      } else {
        const idx = parseInt(preselected.replace("option_", ""), 10) - 1;
        target = opts[Math.max(0, idx)] || opts[0];
      }
      // Closed is the CSS baseline (grid-template-rows:0fr); just clear .selected.
      opts.forEach(function (o) {
        o.classList.remove("selected");
      });
      target.classList.add("selected");
      const inp = target.querySelector("input.quantity-break-input");
      if (inp) inp.checked = true;

      // Enable transitions only after this frame so the preselected tier is open
      // instantly on load (no animation flash), while all later toggles animate.
      requestAnimationFrame(function () {
        rootEl.classList.add("qb-anim-ready");
      });
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 9. LAYOUT HELPERS
     * ───────────────────────────────────────────────────────────────────── */

    function stampDataAttrs() {
      rootEl.querySelectorAll(".quantity-break-option-container").forEach(function (container) {
        const qty = container.dataset.optionQuantity;
        if (!qty) return;

        // Each option carries its own custom-price format from the child block;
        // fall back to "amount" only if it's somehow missing.
        const optInp = container.querySelector("input.quantity-break-input");
        if (optInp && !optInp.dataset.customPriceFormat) {
          optInp.dataset.customPriceFormat = "amount";
        }

        container.querySelectorAll(".quantity-break-variant-selectors").forEach(function (row, i) {
          row.dataset.quantityOption = qty;
          row.dataset.itemIndex = String(i + 1);
          row.querySelectorAll("[data-option-selector]").forEach(function (inp2) {
            inp2.dataset.quantityOption = qty;
            inp2.dataset.itemIndex = String(i + 1);
          });
        });
      });
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 10. SVP SYNC
     * ───────────────────────────────────────────────────────────────────── */

    // Push QB selection back to simple-variant-picker labels so they stay in sync
    function syncSVPDisplay(qtyOpt) {
      rootEl
        .querySelectorAll('.quantity-break-variant-select[data-quantity-option="' + qtyOpt + '"]')
        .forEach(function (sel) {
          const pos = sel.dataset.optionSelector;
          const val = sel.value;
          if (!val || !pos) return;
          const lbl = document.querySelector(
            '.simple-variant-picker__selected-variant[data-option-position="' + pos + '"]',
          );
          if (lbl) lbl.textContent = val;
          const optName = optionNames[parseInt(pos, 10) - 1];
          if (optName) {
            const radio = document.querySelector('input[name="options[' + optName + ']"][value="' + val + '"]');
            if (radio && !radio.checked) radio.checked = true;
          }
        });
    }

    // Pull SVP state into QB selectors when the SVP changes
    function updateQBFromSVP(qtyOpt, pos, val) {
      rootEl
        .querySelectorAll(
          '.quantity-break-variant-select[data-quantity-option="' + qtyOpt + '"][data-option-selector="' + pos + '"]',
        )
        .forEach(function (s) {
          const hasVal = Array.from(s.options).some(function (o) {
            return o.value === val;
          });
          if (!hasVal) return;
          // simple-variant-picker may have already written the value to the
          // hidden select, so always refresh the visible button and swatches.
          if (s.value !== val) s.value = val;
          syncImageDropdownButton(s);
          syncSwatches(s);
        });
    }

    // Sync all item rows for the selected option tier from a given variant object
    function syncAllRowsFromVariant(variant, qtyOpt) {
      if (!variant || !qtyOpt) return;
      for (let p = 1; p <= optionNames.length; p++) {
        const val = variant["option" + p];
        if (!val) continue;
        updateQBFromSVP(qtyOpt, p, val);
      }
    }

    function revealVariantMedia(mediaId) {
      const strip = document.querySelector(".shop-thumbnails.toggle-product-variants");
      if (!strip) return;
      const next = strip.querySelector('.shop-thumbnail[data-media-id="' + mediaId + '"]');
      if (!next) return;
      const current = strip.querySelector(".current-variant-media");
      if (current === next) return;
      if (current) {
        current.classList.add("visually-hidden");
        current.classList.remove("current-variant-media");
      }
      next.classList.remove("visually-hidden");
      next.classList.add("current-variant-media");
    }

    function notifyVariantImage(variant) {
      if (!syncGallery) return;
      const mediaId = variant && variant.featured_media && variant.featured_media.id;
      if (!mediaId) return;
      const active = document.querySelector(".shop-main-image.active[data-media-id]");
      if (active && active.dataset.mediaId === String(mediaId)) return;
      revealVariantMedia(mediaId);
      document.dispatchEvent(
        new CustomEvent("variant:imageChanged", {
          detail: {
            variantId: variant.id,
            mediaId: mediaId,
          },
        }),
      );
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 11. EVENT BINDING
     * ───────────────────────────────────────────────────────────────────── */

    // Tracked document listeners so we can cleanly remove them on teardown
    const _docListeners = [];
    function addDocListener(event, handler) {
      document.addEventListener(event, handler);
      _docListeners.push({ event: event, handler: handler });
    }

    function removeAllDocListeners() {
      _docListeners.forEach(function (l) {
        document.removeEventListener(l.event, l.handler);
      });
      _docListeners.length = 0;
    }

    function bindClicks() {
      rootEl.addEventListener("click", function (e) {
        // Don't trigger option selection when the user is interacting with variant selectors
        if (e.target.closest(".quantity-break-variant-selection")) return;
        // Don't re-select (and re-run the open/close animation) when interacting with info bars
        if (e.target.closest(".qb-bars-container")) return;
        const optEl = e.target.closest(".quantity-break-option");
        if (!optEl || !rootEl.contains(optEl)) return;
        // Already-selected option: no-op so we don't replay the open animation
        if (optEl.classList.contains("selected")) return;
        selectOption(optEl);
      });
    }

    function bindVariantSelectors() {
      if (!enableVS) return;
      rootEl.addEventListener("change", function (e) {
        const t = e.target;
        if (!t.matches(".quantity-break-variant-select")) return;

        const qtyOpt = t.dataset.quantityOption;

        updateVariantSelections();
        syncSVPDisplay(qtyOpt);
        notifyVariantImage(pickVariant(qtyOpt, t.dataset.itemIndex || 1));

        // Notify price-sync and other consumers that QB state changed
        document.dispatchEvent(
          new CustomEvent("quantity_break:changed", {
            detail: {
              quantity: parseInt(qtyOpt, 10) || 1,
              price: 0,
              blockId: blockId,
            },
          }),
        );
      });
    }

    function bindGiftSelects() {
      rootEl.addEventListener("change", function (e) {
        const t = e.target;
        if (!t.matches(".qb-gift-variant-select")) return;
        const bar = t.closest(".qb-bar[data-bar-type='free_gift']");
        if (bar) {
          bar.dataset.giftVariantId = t.value;
          // Swap the left thumbnail to the selected variant's image
          const opt = t.options[t.selectedIndex];
          const img = bar.querySelector(".qb-bar-gift-img");
          const src = opt && opt.dataset.variantImage;
          if (img && src) img.src = src;
        }
        updateVariantSelections();
      });
    }

    // Format delivery-date bars (static; runs once on init). Countdown bars are
    // handled by the global countdown-controller via [data-countdown].
    function hydrateBarDates() {
      rootEl.querySelectorAll(".qb-bar-date[data-timestamp]").forEach(function (el) {
        try {
          const ts = parseInt(el.dataset.timestamp, 10) * 1000;
          const locale = el.dataset.locale || "en";
          const fmt = el.dataset.format || "short";
          const opts =
            fmt === "long"
              ? { weekday: "long", month: "long", day: "numeric" }
              : { month: "short", day: "numeric" };
          el.textContent = new Intl.DateTimeFormat(locale, opts).format(new Date(ts));
        } catch (e) {
          /* noop */
        }
      });
    }

    function bindExternalEvents() {
      // SVP variant change → sync QB selectors + rebuild form
      addDocListener("simple-variant-picker:variantChange", function (e) {
        const v = e.detail && e.detail.variant;
        if (v && typeof Elixir_SetProductFormVariantId === "function") {
          Elixir_SetProductFormVariantId(v.id);
        }
        if (enableVS && v) {
          const inp = rootEl.querySelector(".quantity-break-option.selected input.quantity-break-input");
          const qtyOpt = inp ? inp.value : null;
          if (qtyOpt) syncAllRowsFromVariant(v, qtyOpt);
        }
        updateVariantSelections();
      });

      // Subscription context change → rebuild items[] with new selling_plan + reprice
      addDocListener("subscriptionContext:updated", function () {
        updateVariantSelections();
      });

      // quantity_break:changed from OTHER blocks only (skip own echo)
      addDocListener("quantity_break:changed", function (e) {
        if (e.detail && e.detail.blockId === blockId) return;
        updateVariantSelections();
      });

      // Pricing helpers became available → run first price render
      addDocListener("elixir-pricing-ready", function () {
        scheduleRefresh();
      });

      // External quantity:changed (e.g. from quantity selector stepper) → select
      // the matching QB tier if one exists. Skip own-sourced events to avoid loops.
      addDocListener("quantity:changed", function (e) {
        if (e.detail && e.detail.source === blockId) return;
        var qty = e.detail && e.detail.quantity;
        if (!qty || qty < 1) return;

        // Find the tier whose quantity matches
        var matchingOption = null;
        rootEl.querySelectorAll(".quantity-break-option").forEach(function (optEl) {
          var inp = optEl.querySelector("input.quantity-break-input");
          if (inp && parseInt(inp.value, 10) === qty) {
            matchingOption = optEl;
          }
        });

        if (!matchingOption) {
          // No tier matches this quantity — keep the selection but drop the
          // override so the price block resumes its own quantity handling.
          scheduleRefresh();
          return;
        }

        // Only act if this tier isn't already selected (prevents re-firing)
        if (matchingOption.classList.contains("selected")) return;

        selectOption(matchingOption);
      });
    }

    /* ─── Shopify Theme Editor ─────────────────────────────────────────── */

    function bindEditorEvents() {
      if (!window.Shopify || !Shopify.designMode) return;

      function isRelatedEvent(event) {
        if (!event || !event.target) return false;
        return event.target.contains(rootEl) || rootEl.contains(event.target);
      }

      // Section reloaded in editor → re-query root from fresh DOM and re-init
      document.addEventListener("shopify:section:load", function (event) {
        if (!isRelatedEvent(event)) return;
        const freshRoot = document.getElementById("qb-" + blockId);
        if (!freshRoot) return;
        removeAllDocListeners();
        initBlock(freshRoot);
      });

      // Section removed from editor → clean up all listeners + clear price override
      document.addEventListener("shopify:section:unload", function (event) {
        if (!isRelatedEvent(event)) return;
        removeAllDocListeners();
        if (window.Elixir_PriceOverride) {
          window.Elixir_PriceOverride.clear(blockId);
        }
      });

      // Child option block selected in editor sidebar:
      // → scroll it into view AND make it the active tier
      document.addEventListener("shopify:block:select", function (event) {
        if (!event.target) return;
        if (!rootEl.contains(event.target)) return;
        const optContainer =
          event.target.querySelector(".quantity-break-option-container") ||
          event.target.closest(".quantity-break-option-container");
        const optEl = optContainer && optContainer.querySelector(".quantity-break-option");
        if (optEl) {
          selectOption(optEl);
          optContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });

      // Child option block deselected in editor sidebar → no-op (here for future use)
      document.addEventListener("shopify:block:deselect", function (event) {
        if (!event.target || !rootEl.contains(event.target)) return;
      });
    }

    /* ─────────────────────────────────────────────────────────────────────
     * 12. INIT ORCHESTRATION
     * ───────────────────────────────────────────────────────────────────── */

    // Phase 1 — form-independent setup. Selecting the preselected tier (the
    // .selected class + checked radio + open card) and painting the save badge /
    // prices do NOT need the product form, so this runs immediately and is never
    // blocked by form detection.
    function initVisual() {
      stampDataAttrs();
      applyPreselection();
      initLayout();
      initImageDropdowns();
      initSwatches();

      bindClicks();
      bindVariantSelectors();
      bindGiftSelects();
      bindExternalEvents();
      bindEditorEvents();

      hydrateBarDates();

      // Trigger an initial price/badge render — either immediately if pricing
      // helpers are already loaded, or deferred until the elixir-pricing-ready
      // event. refreshPrices() falls back to defaultVariant when the form is
      // absent, so the badge paints without waiting on the form.
      if (window.__elixirPricingReady) {
        scheduleRefresh();
      }
      // The elixir-pricing-ready listener in bindExternalEvents covers the deferred case.
    }

    // Phase 2 — form-dependent commit. Pushes the preselected tier's quantity,
    // items[] payload, and change events (quantity:changed / quantity_break:changed)
    // into the shared product form so price-sync, ATC, and the sticky ATC reflect
    // the selection. Uses commitSelection() (not selectOption) to avoid replaying
    // the open animation already applied in Phase 1.
    function commitToForm() {
      const initialSelected = rootEl.querySelector(".quantity-break-option.selected");
      if (initialSelected) {
        commitSelection(initialSelected);
      } else {
        updateVariantSelections();
      }
    }

    function tryInit() {
      // Visual setup never waits on the product form.
      initVisual();

      // Commit to the product form as soon as it exists.
      if (typeof Elixir_ProductFormExists !== "function" || Elixir_ProductFormExists()) {
        commitToForm();
        return;
      }

      // Form not present yet: resolve fast via a MutationObserver + a short poll,
      // instead of a multi-second blind wait. Whichever fires first wins.
      let done = false;
      let observer = null;
      let pollId = null;
      let timeoutId = null;

      function cleanup() {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (pollId) {
          clearInterval(pollId);
          pollId = null;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      }

      function run() {
        if (done) return;
        done = true;
        cleanup();
        commitToForm();
      }

      if (typeof MutationObserver !== "undefined") {
        observer = new MutationObserver(function () {
          if (Elixir_ProductFormExists()) run();
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }

      // Fast poll — resolves the common case (form already in the DOM but no new
      // mutation to wake the observer) within one interval instead of seconds.
      pollId = setInterval(function () {
        if (Elixir_ProductFormExists()) run();
      }, 60);

      // Hard ceiling — commit regardless so getCurrentVariant()'s defaultVariant
      // fallback still applies and the UI is never permanently stuck.
      timeoutId = setTimeout(run, 2000);
    }

    tryInit();
  }

  /* ─────────────────────────────────────────────────────────────────────────
   * ENTRY POINT
   * Finds all QB root elements on the page and initialises each one.
   * Runs on DOMContentLoaded or immediately if the DOM is already ready.
   * ───────────────────────────────────────────────────────────────────────── */
  function boot() {
    document.querySelectorAll(".quantity-break-container[data-block-id]").forEach(function (rootEl) {
      // Guard against double-init (can happen if the script is deferred and
      // DOMContentLoaded fires before the element is stamped with data-block-id).
      if (rootEl.__qbInitialised) return;
      rootEl.__qbInitialised = true;
      initBlock(rootEl);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
